import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { MessageCircle, Send, Heart } from 'lucide-react';
import './Prayer.css';

const Prayer = () => {
  const { user } = useAuth();
  const [prayerRequest, setPrayerRequest] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setPrayerRequest('');
      setSubmitted(false);
    }, 3000);
  };

  const prayerCategories = [
    {
      title: "Healing",
      english: "Lord, we pray for physical, emotional, and spiritual healing.",
      telugu: "ప్రభువా, శారీరక, మానసిక మరియు ఆధ్యాత్మిక స్వస్థత కోసం మేము ప్రార్థిస్తున్నాము."
    },
    {
      title: "Guidance",
      english: "Father, guide us in Your wisdom and show us Your path.",
      telugu: "తండ్రీ, మీ జ్ఞానంలో మమ్మల్ని నడిపించండి మరియు మీ మార్గాన్ని చూపించండి."
    },
    {
      title: "Strength",
      english: "God, grant us strength to face challenges with faith.",
      telugu: "దేవా, విశ్వాసంతో సవాళ్లను ఎదుర్కొనే శక్తిని మాకు ప్రసాదించండి."
    },
    {
      title: "Peace",
      english: "Prince of Peace, fill our hearts with Your perfect peace.",
      telugu: "శాంతి రాజా, మీ పరిపూర్ణ శాంతితో మా హృదయాలను నింపండి."
    }
  ];

  const prayerStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Prayer Requests & Christian Prayer Guide - HolyBible365",
  "description": "Submit prayer requests, find prayer guidance, and join a community of faith. Available in English and Telugu with prayer focus areas.",
  "url": "https://holybible365.com/prayer",
  "isPartOf": {
    "@type": "WebSite",
    "name": "HolyBible365",
    "url": "https://holybible365.com"
  },
  "mainEntity": {
    "@type": "WebApplication",
    "name": "Christian Prayer Platform",
    "description": "Platform for submitting prayer requests and finding Christian prayer guidance",
    "inLanguage": ["en", "te"]
  }
};

return (
    <>
      <SEO 
        title="Prayer Requests & Christian Prayer Guide"
        description="Submit prayer requests, find prayer guidance, and join a community of faith. Available in English and Telugu with prayer focus areas and effective prayer tips."
        keywords="prayer requests, Christian prayer, prayer guide, faith community, spiritual prayer, Bible prayer, Telugu prayer"
        canonicalUrl="https://holybible365.com/prayer"
        structuredData={prayerStructuredData}
      />
      <Navbar />
      <div className="prayer-container">
        <div className="container">
          <div className="prayer-header card">
            <MessageCircle size={48} />
            <h1>Prayer Request</h1>
            <p>Share your prayer requests with our community</p>
            {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
              <p className="telugu">మీ ప్రార్థన అభ్యర్థనలను మా సంఘంతో పంచుకోండి</p>
            )}
          </div>

          <div className="prayer-form-section card">
            <h2>Submit Your Prayer Request</h2>
            {submitted ? (
              <div className="success-message">
                <Heart size={32} />
                <p>Thank you! Your prayer request has been received.</p>
                <p>We will pray for you. God bless you!</p>
                {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
                  <p className="telugu">ధన్యవాదాలు! మీ ప్రార్థన అభ్యర్థన అందుకుంది. మేము మీ కోసం ప్రార్థిస్తాము.</p>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="prayer-form">
                <div className="input-group">
                  <label htmlFor="prayerRequest">
                    Your Prayer Request
                    {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
                      <span className="telugu-label"> (మీ ప్రార్థన అభ్యర్థన)</span>
                    )}
                  </label>
                  <textarea
                    id="prayerRequest"
                    value={prayerRequest}
                    onChange={(e) => setPrayerRequest(e.target.value)}
                    placeholder="Share what's on your heart..."
                    rows="6"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-large">
                  <Send size={20} />
                  Submit Prayer Request
                </button>
              </form>
            )}
          </div>

          <div className="prayer-categories-section">
            <h2>Prayer Focus Areas</h2>
            <div className="categories-grid">
              {prayerCategories.map((category, index) => (
                <div key={index} className="category-card card">
                  <h3>{category.title}</h3>
                  <p>{category.english}</p>
                  {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
                    <p className="telugu">{category.telugu}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="prayer-guide card">
            <h2>How to Pray Effectively</h2>
            <div className="guide-content">
              <div className="guide-item">
                <strong>1. Start with Worship</strong>
                <p>Begin by praising God for who He is and what He has done.</p>
                {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
                  <p className="telugu">దేవుడు ఎవరు మరియు ఆయన ఏమి చేశాడో స్తుతించడం ద్వారా ప్రారంభించండి.</p>
                )}
              </div>

              <div className="guide-item">
                <strong>2. Confess Your Sins</strong>
                <p>Acknowledge your shortcomings and ask for forgiveness.</p>
                {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
                  <p className="telugu">మీ లోపాలను అంగీకరించండి మరియు క్షమాపణ అడగండి.</p>
                )}
              </div>

              <div className="guide-item">
                <strong>3. Give Thanks</strong>
                <p>Express gratitude for God's blessings in your life.</p>
                {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
                  <p className="telugu">మీ జీవితంలో దేవుని ఆశీర్వాదాలకు కృతజ్ఞతలు తెలపండి.</p>
                )}
              </div>

              <div className="guide-item">
                <strong>4. Make Your Requests</strong>
                <p>Bring your needs and concerns to God with faith.</p>
                {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
                  <p className="telugu">విశ్వాసంతో మీ అవసరాలను మరియు ఆందోళనలను దేవునికి తెలియజేయండి.</p>
                )}
              </div>
            </div>
          </div>

          <div className="prayer-promise card">
            <h2>God's Promise About Prayer</h2>
            <blockquote>
              "Ask, and it shall be given you; seek, and ye shall find; knock, and it shall be opened unto you."
              <cite>— Matthew 7:7</cite>
            </blockquote>
            {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
              <blockquote className="telugu">
                "అడుగుడి, మీకు ఇయ్యబడును; వెదకుడి, మీరు కనుగొందురు; తట్టుడి, మీకు తెరువబడును."
                <cite>— మత్తయి 7:7</cite>
              </blockquote>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Prayer;
